// Custom Reducer used for filtering agenda items
import { useState } from "react";
import { atom, useRecoilState, useSetRecoilState } from "recoil";

export const engageFilteredState = atom({
  key: "engageFiltered",
  default: [],
});

import {
  engageTimeSpanState,
  engageAvailableDeps,
  engageMeetingsState,
  engageDepartmentState,
} from "../../atoms/engage";

import { apiGetAgendas } from "../api/engage";
import { apiGetAllBoards } from "../api/boards";
import { calculateDuration } from "../../helpers/timing";

export default function useEngageFilter() {
  const [filteredMeetings, setFilteredMeetings] =
    useRecoilState(engageFilteredState);
  const [fetching, setFetching] = useState(false);
  const [filterText, setFilterText] = useState({ board: "", date: "" });
  const [selected, setSelected] = useRecoilState(engageDepartmentState);
  const [selectedDate, setSelectedDate] = useRecoilState(engageTimeSpanState);
  const setDepartments = useSetRecoilState(engageAvailableDeps);
  const [meetingList, setMeetingList] = useRecoilState(engageMeetingsState);

  function filterMeetings(boardKey, dateKey) {
    // Check if both filters are empty
    if (boardKey === "" && dateKey === "") {
      // Set filtered meetings to a specific default array (e.g., `defaultMeetingList`)
      setFilteredMeetings(meetingList);
      return; // Exit the function early
    }

    // Apply the filters
    const results = filteredMeetings.filter((meeting) => {
      const boardMatch = meeting.department?.name
        .toLowerCase()
        .includes(boardKey);
      // const dateMatch =
      //   dateKey === "" || meeting.scheduleDate.toLowerCase().includes(dateKey);
      return boardMatch;
    });

    setFilteredMeetings(results);
  }

  function applyCombinedFilters(boardKey, dateKey) {
    const boardLower = boardKey.toLowerCase();
    const dateLower = dateKey.toLowerCase();

    const results = meetingList.filter((meeting) => {
      const boardMatch =
        !boardLower ||
        meeting.department?.name.toLowerCase().includes(boardLower);
      const dateMatch =
        !dateLower || meeting.scheduleDate.toLowerCase().includes(dateLower);
      return boardMatch && dateMatch;
    });

    setFilteredMeetings(results);
  }

  async function applyFilter(action) {
    switch (action.type) {
      case "FETCH_DATA":
        setFetching(true);
        const { client, date, department } = action.payload;
        const result = await apiGetAgendas(client, date, department);
        let fetchList = [];
        if (result) {
          for (const item of result) {
            // cacluclate duration
            const duration = calculateDuration(item.actionTopics);
            fetchList.push({ ...item, duration });
          }
          setMeetingList(fetchList);
          setFilteredMeetings(fetchList);
          setFetching(false);
        } else {
          setMeetingList([]);
          setFilteredMeetings([]);
          setFetching(false);
        }
        // fetch boards
        const boardResults = await apiGetAllBoards(client);
        if (boardResults) {
          setDepartments(boardResults);
          if (action.payload.board) {
            const index = boardResults
              .findIndex((e) => e.id.toString() === action.payload.board)
              .toString();
            if (index >= 0) {
              setSelected(boardResults[index]);
            } else {
              setSelected({ name: "All", id: "all" });
            }
          } else {
            setSelected({ name: "All", id: "all" });
          }
        }
        break;
      case "RESET":
        setFilterText({ board: "", date: "" });
        break;
      case "SET_BOARD":
        setFilterText({ board: "", date: "" });
        setSelected(action.payload);
        setMeetingList([]);
        setFilteredMeetings([]);
        const boardResult = await apiGetAgendas(
          action.client,
          selectedDate.id,
          action.payload.id,
        );
        let boardResultList = [];
        if (boardResult) {
          if (boardResult.length === 0) {
            setMeetingList([]);
            setFilteredMeetings([]);
          } else {
            for (const item of boardResult) {
              // cacluclate duration
              const duration = calculateDuration(item.actionTopics);
              boardResultList.push({ ...item, duration });
            }
            setMeetingList(boardResultList);
            setFilteredMeetings(boardResultList);
          }
        }
        break;
      case "SET_DATE":
        setFilterText({ board: "", date: "" });
        setSelectedDate(action.payload);
        setMeetingList([]);
        setFilteredMeetings([]);
        const dateResult = await apiGetAgendas(
          action.client,
          action.payload.id,
          selected.id,
        );
        let dateResultList = [];
        if (dateResult) {
          if (dateResult.length === 0) {
            setMeetingList([]);
            setFilteredMeetings([]);
          } else {
            for (const item of dateResult) {
              // cacluclate duration
              const duration = calculateDuration(item.actionTopics);
              dateResultList.push({ ...item, duration });
            }
            setMeetingList(dateResultList);
            setFilteredMeetings(dateResultList);
          }
        }
        break;
      case "FILTER_BOARD":
        const newBoard = action.payload;
        const currentDate = filterText.date;

        setFilterText({ ...filterText, board: newBoard });
        applyCombinedFilters(newBoard, currentDate);
        // setFilterText({ ...filterText, board: action.payload });

        // const boardKey = action.payload;
        // const boardLowerKey = boardKey.toLowerCase();
        // // TODO need to take into account the date
        // if (boardKey !== "") {
        //   const results = meetingList.filter((y) => {
        //     let item = y.meetingType.toLowerCase();
        //     return item.indexOf(boardLowerKey) != -1;
        //   });
        //   setFilteredMeetings(results);
        // } else {
        //   if (filterText.date !== "") {
        //     const dateResults = meetingList.filter((y) => {
        //       let item = y.scheduleDate.toLowerCase();
        //       return item.indexOf(filterText.date.toLowerCase()) != -1;
        //     });
        //     setFilteredMeetings(dateResults);
        //   } else {
        //     setFilteredMeetings(meetingList);
        //   }
        // }
        break;
      case "FILTER_DATE":
        const newDate = action.payload;
        const currentBoard = filterText.board;

        setFilterText({ ...filterText, date: newDate });
        applyCombinedFilters(currentBoard, newDate);
        // setFilterText({ ...filterText, date: action.payload });

        // const dateKey = action.payload;
        // const dateLowerKey = dateKey.toLowerCase();
        // if (dateKey !== "") {
        //   const dateResults = meetingList.filter((y) => {
        //     let item = y.scheduleDate.toLowerCase();
        //     return item.indexOf(dateLowerKey) != -1;
        //   });
        //   setFilteredMeetings(dateResults);
        // } else {
        //   if (filterText.board !== "") {
        //     const dateResults = meetingList.filter((y) => {
        //       let item = y.department?.name.toLowerCase();
        //       return item.indexOf(filterText.board.toLowerCase()) != -1;
        //     });
        //     setFilteredMeetings(dateResults);
        //   } else {
        //     setFilteredMeetings(meetingList);
        //   }
        // }
        break;
      default:
        break;
    }
  }

  return {
    applyFilter,
    filteredMeetings,
    fetching,
    filterText,
  };
}
